import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import type { Column, RowRenderProps, SortingRule } from 'react-table';
import { createStructuredSelector } from 'reselect';
import { Action, ResourceType } from '../../../../../typings/permissions';
import { userHasPermissionToEditTeamUsers } from '../../../../helpers/permissions';
import { roleLabel } from '../../../../helpers/role';
import type { SetUserTeamRolesRequest } from '../../../../../typings/api';
import type { RolesStore } from '../../../../redux/roles';
import RolesModules from '../../../../redux/roles';
import type { ReduxStore } from '../../../../redux/store';
import type { TeamsStore } from '../../../../redux/teams';
import TeamsModule from '../../../../redux/teams';
import type { UsersStore } from '../../../../redux/users';
import UsersModule from '../../../../redux/users';
import type { Team, TeamUser } from '../../../../types';
import Loader from '../../../generic/Loader';
import PopperMenu from '../../../generic/PopperMenu';
import SimpleReactTable from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import TeamRoleSelector from './TeamRoleSelector';
import { window } from '../../../../globals';
import { useDialogContext } from '../../../../contexts/DialogContext';

type ReduxStateProps = {
  users: UsersStore['entities'];
  loadingUpdatingUsers: Set<number>;
  pendingRemovedUsers: TeamsStore['pendingRemovedUsers'];
  roles: RolesStore['entities'];
};

type DispatchProps = {
  setTeamRoles(userId: number, newRoles: SetUserTeamRolesRequest): void;
  removeMembers(teamId: number, users: Array<{ id: number }>): void;
};

type TeamMembersProps = ReduxStateProps &
  DispatchProps & {
    team: Team;
    search: string;
  };

type UserDataRow = {
  userId: number;
  username: string;
  email: string;
  roleId: number;
  role: string;
};

const prepareUserData = (
  teamUsers: Array<Pick<TeamUser, 'userId' | 'roleId'>>,
  users: ReduxStateProps['users'],
  search: string,
  sortMode: SortingRule,
  roles: RolesStore['entities'],
): UserDataRow[] => {
  let rows =
    teamUsers?.map(
      ({ userId, roleId }): UserDataRow => ({
        userId,
        username: users[userId]?.username || users[userId]?.email || 'Unknown',
        email: users[userId]?.email || 'Unknown',
        role: roleLabel(roles, roleId),
        roleId,
      }),
    ) || [];

  if (search) {
    const fields = ['username', 'email', 'role'];
    const searchLower = search.toLowerCase();
    rows = rows.filter((row) => fields.some((field) => row[field].toLowerCase().includes(searchLower)));
  }

  return rows.sort((a, b) => {
    const { id, desc: isDescending } = sortMode;
    if (isDescending) {
      return b[id].toLowerCase().localeCompare(a[id].toLowerCase());
    }
    return a[id].toLowerCase().localeCompare(b[id].toLowerCase());
  });
};

const TeamMembers = ({
  team,
  search,
  users,
  loadingUpdatingUsers,
  pendingRemovedUsers,
  roles,
  setTeamRoles,
  removeMembers,
}: TeamMembersProps) => {
  const [sortMode, setSortMode] = useState<SortingRule>({ id: 'username', desc: false });

  const [userData, setUserData] = useState<UserDataRow[]>([]);
  const { onConfirm } = useDialogContext();

  useEffect(() => {
    setUserData(prepareUserData(team.teamUsers, users, search, sortMode, roles));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: check if this is correct
  }, [team, users, search, sortMode]);

  const canManageRoles = window.hasPermission({ resourceType: ResourceType.Role, action: Action.Manage });

  const canEditUser = (userId: number) =>
    (window.user?.id !== userId || canManageRoles) && userHasPermissionToEditTeamUsers(team.id);

  const onRemoveClick = (userToRemove: UserDataRow) => () =>
    onConfirm({
      title: `Are you sure you want to remove "${userToRemove.username || 'user'}"?`,
      body: `Removed users will no longer be able to view this team's projects.`,
      onConfirm: () => removeMembers(team.id, [{ id: userToRemove.userId }]),
      confirmText: 'Remove',
      confirmBtnType: 'error',
    });

  const memberEditDropdownButton = (userToRemove: UserDataRow) =>
    canEditUser(userToRemove.userId) && (
      <PopperMenu>
        <PopperMenu.Item onClick={onRemoveClick(userToRemove)} isWarning>
          Remove member
        </PopperMenu.Item>
      </PopperMenu>
    );

  const columns: Array<Column<any>> = [
    {
      id: 'username',
      Header: <TableHeader title="Member" sort={{ id: 'username', currentMode: sortMode }} />,
      accessor: 'username',
      width: 310,
      resizable: false,
      style: {
        textOverflow: 'ellipsis',
      },
    },
    {
      id: 'email',
      Header: <TableHeader title="Email" sort={{ id: 'email', currentMode: sortMode }} />,
      accessor: 'email',
      width: 310,
      resizable: false,
      style: {
        textOverflow: 'ellipsis',
      },
    },
    {
      id: 'role',
      accessor: 'role',
      Header: <TableHeader title="Role" sort={{ id: 'role', currentMode: sortMode }} />,
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ original }: { original: UserDataRow } & RowRenderProps) => (
        <>
          {loadingUpdatingUsers.has(original.userId) ? (
            <Loader size={30} style={{ height: '30px' }} />
          ) : (
            <TeamRoleSelector
              roles={roles}
              userId={original.userId}
              roleId={original.roleId}
              teamId={team.id}
              disabled={!canEditUser(original.userId)}
              onSetUserRole={(roleId) => setTeamRoles(original.userId, [{ teamId: team.id, roleId }])}
            />
          )}
          {pendingRemovedUsers.includes(original.userId) ? (
            <Loader size={30} style={{ height: '30px', width: '30px' }} />
          ) : (
            memberEditDropdownButton(original)
          )}
        </>
      ),
      resizable: false,
      style: { overflow: 'visible', paddingRight: '15px' },
    },
  ];

  return (
    <div className="team-settings__team-members">
      <SimpleReactTable
        data={userData}
        columns={columns}
        defaultSortMode={[
          {
            id: 'username',
            desc: false,
          },
        ]}
        onSortedChanged={setSortMode}
        showPagination={userData.length > 20}
        cacheId="teamMembers"
      />
    </div>
  );
};

const mapStateToProps = createStructuredSelector<ReduxStore, ReduxStateProps>({
  users: UsersModule.selectors.getUsers,
  loadingUpdatingUsers: UsersModule.selectors.getLoadingUpdatingUsers,
  pendingRemovedUsers: TeamsModule.selectors.getPendingRemovedUsers,
  roles: RolesModules.selectors.getRoles,
});

const mapDispatchToProps: DispatchProps = {
  setTeamRoles: UsersModule.actions.setTeamRoles,
  removeMembers: TeamsModule.actions.removeTeamUsers,
};

export default connect(mapStateToProps, mapDispatchToProps)(TeamMembers);
